import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2Icon, PlusIcon, Trash2Icon, XCircleIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import ClubEmptyList from '../components/ClubEmptyList';
import DialogToken from '../components/DialogToken';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

export default function ListApiTokens() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();

    const page = Number(searchParams.get('page')) || 1;
    const perPage = Number(searchParams.get('perPage')) || 10;

    const { data, mutate, isFetching } = useFetch('club/api', {
        params: {
            clubId: id,
            page,
            perPage,
        },
    });

    useEffect(() => {
        mutate();
    }, [searchParams, mutate]);

    const handleDelete = async tokenId => {
        try {
            await confirm({
                title: 'Remover token',
                description: 'Tem certeza que deseja remover o token?',
                confirmationText: 'Confirmo',
                confirmationButtonProps: { variant: 'destructive' },
            });

            setLoading(true);
            await server.delete(`club/api/${tokenId}`);
            enqueueSnackbar('Token excluído com sucesso.');
            await mutate();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const rows = data?.rows || [];
    const totalPages = data?.numRows ? Math.ceil(data.numRows / perPage) : 1;

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                    <div>
                        <CardTitle>API Tokens</CardTitle>
                        <CardDescription>Adicione e gerencie seus tokens de acesso à API.</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" render={<Link to="#tokens" />}>
                        <PlusIcon className="size-4" />
                        Adicionar token
                    </Button>
                </CardHeader>
            </Card>

            <Card>
                <CardContent className="p-0">
                    {isFetching && !rows.length ? (
                        <div className="flex justify-center py-10">
                            <Spinner className="size-6" />
                        </div>
                    ) : rows.length === 0 ? (
                        <ClubEmptyList title="Nenhum token cadastrado" />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40 text-left">
                                        <th className="px-4 py-3 font-medium">ID</th>
                                        <th className="px-4 py-3 font-medium">Criado em</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 text-right font-medium">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.id} className="border-b last:border-b-0">
                                            <td className="px-4 py-3">{row.id}</td>
                                            <td className="px-4 py-3 text-muted-foreground">
                                                {row.createdAt
                                                    ? new Date(row.createdAt).toLocaleString('pt-BR')
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.status ? (
                                                    <Badge variant="default" className="gap-1">
                                                        <CheckCircle2Icon className="size-3" />
                                                        Ativo
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="gap-1">
                                                        <XCircleIcon className="size-3" />
                                                        Inativo
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    disabled={loading}
                                                    onClick={() => handleDelete(row.id)}
                                                    aria-label="Excluir token"
                                                >
                                                    <Trash2Icon className="size-4 text-destructive" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 ? (
                        <div className="flex items-center justify-center gap-3 border-t px-4 py-4">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const next = new URLSearchParams(prev);
                                        next.set('page', String(page - 1));
                                        return next;
                                    })
                                }
                            >
                                Anterior
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Página {page} de {totalPages}
                            </span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() =>
                                    setSearchParams(prev => {
                                        const next = new URLSearchParams(prev);
                                        next.set('page', String(page + 1));
                                        return next;
                                    })
                                }
                            >
                                Próxima
                            </Button>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <DialogToken mutate={mutate} />
        </div>
    );
}
